import { router, useLocalSearchParams } from 'expo-router';
import { Fragment, createElement, useCallback, useEffect, useRef, useState } from 'react';

import { requestOrderTypeFocusHandoff } from '@fhs/routing/src/service-mode-dialog/order-type-focus-handoff';
import { getFirstSearchParam } from '@fhs/utils';
import { SwitchServiceModeDialog } from '@fhs-legacy/frontend/src/components/switch-service-mode-dialog';
import {
  IDeliveryFee,
  useDeliveryRestaurantLazyQuery,
} from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import useDialogModal from '@fhs-legacy/frontend/src/hooks/use-dialog-modal';
import useErrorModal from '@fhs-legacy/frontend/src/hooks/use-error-modal';
import {
  DeliveryStoreStatusToStoreStatusMap,
  IGetClosestAvailableDeliveryRestaurantResult,
  StoreStatus,
} from '@fhs-legacy/frontend/src/remote/api/restaurants';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { ServiceMode, useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { convertMilesToMeters } from '@fhs-legacy/frontend/src/utils/distance';
import { platform } from '@fhs-legacy/frontend/src/utils/environment';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';
import { IStore } from '@rbi-ctg/store';

import {
  DeliveryAddressSelection,
  DeliveryValidationStatus,
} from '../../components/delivery/types';

import {
  DeliveryAddressValidation,
  DeliveryQuoteOutcomeType,
  DeliveryRestaurant,
  InvalidPhoneNumberAlert,
} from './types';
import {
  DELIVERY_CLOSED_MESSAGE,
  DELIVERY_CLOSED_TITLE,
  INVALID_PHONE_NUMBER_ALERT,
  QUOTE_ERROR_MESSAGE,
  getDeliveryQuoteOutcome,
  getDeliveryStoreAssignedAttributes,
  hasInvalidPhoneNumberError,
} from './utils';

/**
 * The quote behind "Deliver Here": finds the restaurant that can deliver to the address, makes it
 * the order's store in delivery mode and moves on to the menu.
 *
 * `status` stays `pending` through a successful quote until the guest has left the locator, so
 * the button keeps its spinner while the store is being selected.
 */
export function useDeliveryAddressValidation(): DeliveryAddressValidation {
  const params = useLocalSearchParams<{ back?: string | string[] }>();
  const back = getFirstSearchParam(params.back);

  const { user } = useAuthContext();
  const { updateDeliveryFees } = useServiceModeContext();
  const { logDeliveryStoreAssigned } = useCRMEventsContext();
  const {
    cartEntries,
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    setQuoteId,
    selectStore,
    selectServiceMode,
  } = useOrderContext();

  const deliveryRadius = useFlag('delivery-radius-in-miles');

  const [queryDeliveryRestaurant] = useDeliveryRestaurantLazyQuery({ fetchPolicy: 'no-cache' });

  const [status, setStatus] = useState(DeliveryValidationStatus.IDLE);
  const [selectionAwaitingCartReset, setSelectionAwaitingCartReset] =
    useState<DeliveryAddressSelection | null>(null);

  const [AlertDialog, openAlert, alertMessage] = useDialogModal<InvalidPhoneNumberAlert>({
    onConfirm: () => router.navigate(`${routes.account}/info`),
    modalAppearanceEventMessage: 'Alert: Payment Failed',
  });
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Delivery Quote Error',
  });

  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Set synchronously, unlike `status`, so a second tap in the same tick is still ignored.
  const isQuotingRef = useRef(false);
  // Bumped by every quote and by `reset`, so a response that arrives after either is dropped.
  const requestIdRef = useRef(0);

  const settle = useCallback(
    (nextStatus: Exclude<DeliveryValidationStatus, DeliveryValidationStatus.PENDING>) => {
      isQuotingRef.current = false;
      if (isMountedRef.current) {
        setStatus(nextStatus);
      }
    },
    []
  );

  const failQuote = useCallback(
    (error?: unknown) => {
      settle(DeliveryValidationStatus.IDLE);
      if (!isMountedRef.current) {
        return;
      }

      openErrorDialog({
        message: QUOTE_ERROR_MESSAGE,
        error: error instanceof Error ? error : undefined,
      });
    },
    [openErrorDialog, settle]
  );

  const showInvalidPhoneNumberAlert = useCallback(() => {
    settle(DeliveryValidationStatus.IDLE);
    if (!isMountedRef.current) {
      return;
    }

    openAlert(INVALID_PHONE_NUMBER_ALERT);
  }, [openAlert, settle]);

  const continueWithStore = useCallback(
    async (
      store: IStore,
      { address, coordinates }: DeliveryAddressSelection,
      phoneNumber: string
    ) => {
      try {
        await selectStore(
          store,
          async () => {
            // `selectStore` only awaits this callback when the order is already in delivery mode.
            try {
              setDeliveryAddress({
                ...address,
                // Saving the address is opted into from checkout.
                shouldSave: undefined,
                latitude: coordinates.lat,
                longitude: coordinates.lng,
              });
              setDeliveryInstructions('');
              setOrderPhoneNumber(phoneNumber);
              selectServiceMode(ServiceMode.DELIVERY);

              requestOrderTypeFocusHandoff();
              router.replace(back ?? routes.menu);
            } catch (error) {
              failQuote(error);
            }
          },
          ServiceMode.DELIVERY
        );
      } catch (error) {
        failQuote(error);
      }
    },
    [
      back,
      failQuote,
      selectServiceMode,
      selectStore,
      setDeliveryAddress,
      setDeliveryInstructions,
      setOrderPhoneNumber,
    ]
  );

  const handleQuoteResult = useCallback(
    (
      selection: DeliveryAddressSelection,
      phoneNumber: string,
      result: IGetClosestAvailableDeliveryRestaurantResult
    ) => {
      const outcome = getDeliveryQuoteOutcome(result);

      switch (outcome.type) {
        case DeliveryQuoteOutcomeType.SUCCESS:
          void continueWithStore(outcome.store, selection, phoneNumber);
          return;
        case DeliveryQuoteOutcomeType.UNAVAILABLE:
          settle(DeliveryValidationStatus.UNAVAILABLE);
          return;
        case DeliveryQuoteOutcomeType.ERROR:
          failQuote();
          return;
        // The legacy modal routed these to screens the app no longer has, so the locator answers
        // them in place.
        case DeliveryQuoteOutcomeType.STORE_STATUS:
          if (outcome.storeStatus === StoreStatus.NO_DELIVERY) {
            settle(DeliveryValidationStatus.UNAVAILABLE);
            return;
          }

          if (outcome.storeStatus === StoreStatus.CLOSED) {
            settle(DeliveryValidationStatus.IDLE);
            openErrorDialog({
              title: DELIVERY_CLOSED_TITLE,
              message: DELIVERY_CLOSED_MESSAGE,
              modalAppearanceEventMessage: 'Error: Delivery Stores Closed',
            });
            return;
          }

          failQuote();
      }
    },
    [continueWithStore, failQuote, openErrorDialog, settle]
  );

  const handleDeliveryRestaurant = useCallback(
    (selection: DeliveryAddressSelection, phoneNumber: string, response: DeliveryRestaurant) => {
      const { quote, restaurant, storeStatus, nextEarliestOpen, quoteId, fees } = response;

      if (restaurant) {
        logDeliveryStoreAssigned(
          getDeliveryStoreAssignedAttributes(restaurant, fees?.totalFeeCents ?? 0)
        );
      }

      if (fees?.totalFeeCents) {
        updateDeliveryFees({
          deliveryTotalFee: fees.totalFeeCents,
          fees: fees.itemizedFees ? (fees.itemizedFees as IDeliveryFee[]) : [],
        });
      }

      setQuoteId(quoteId);

      handleQuoteResult(selection, phoneNumber, {
        deliveryQuote: quote,
        store: (restaurant ?? null) as unknown as IStore | null,
        storeStatus: DeliveryStoreStatusToStoreStatusMap[storeStatus],
        nextEarliestOpen: nextEarliestOpen ? new Date(nextEarliestOpen) : undefined,
      });
    },
    [handleQuoteResult, logDeliveryStoreAssigned, setQuoteId, updateDeliveryFees]
  );

  const requestQuote = useCallback(
    async (selection: DeliveryAddressSelection) => {
      if (isQuotingRef.current) {
        return;
      }

      isQuotingRef.current = true;
      const requestId = ++requestIdRef.current;
      setStatus(DeliveryValidationStatus.PENDING);

      const { address, coordinates } = selection;
      const phoneNumber = user?.details?.phoneNumber || '';

      try {
        const { data, error } = await queryDeliveryRestaurant({
          variables: {
            dropoff: {
              ...address,
              latitude: coordinates.lat,
              longitude: coordinates.lng,
              phoneNumber,
            },
            searchRadius: convertMilesToMeters(deliveryRadius),
            platform: platform(),
          },
        });

        if (requestId !== requestIdRef.current || !isMountedRef.current) {
          return;
        }

        if (hasInvalidPhoneNumberError(error)) {
          showInvalidPhoneNumberAlert();
          return;
        }

        if (!data?.deliveryRestaurant) {
          failQuote(error);
          return;
        }

        handleDeliveryRestaurant(selection, phoneNumber, data.deliveryRestaurant);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          failQuote(error);
        }
      }
    },
    [
      deliveryRadius,
      failQuote,
      handleDeliveryRestaurant,
      queryDeliveryRestaurant,
      showInvalidPhoneNumberAlert,
      user?.details?.phoneNumber,
    ]
  );

  const validate = useCallback(
    (selection: DeliveryAddressSelection) => {
      if (isQuotingRef.current) {
        return;
      }

      if (cartEntries.length > 0) {
        setSelectionAwaitingCartReset(selection);
        return;
      }

      void requestQuote(selection);
    },
    [cartEntries.length, requestQuote]
  );

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    settle(DeliveryValidationStatus.IDLE);
  }, [settle]);

  const dialogs = createElement(
    Fragment,
    null,
    selectionAwaitingCartReset &&
      createElement(SwitchServiceModeDialog, {
        showDialog: true,
        onConfirm: () => void requestQuote(selectionAwaitingCartReset),
        onDismiss: () => setSelectionAwaitingCartReset(null),
      }),
    createElement(AlertDialog, alertMessage ?? {}),
    createElement(ErrorDialog)
  );

  return { status, validate, reset, dialogs };
}
